export interface ChatBadge {
  set: string
  version: string
}

export interface ChatEmote {
  id: string
  start: number
  end: number
}

export type ChatPlatform = 'TWITCH' | 'YOUTUBE'

export interface ChatMessage {
  id: string
  platform: ChatPlatform
  channel: string
  roomId: string
  login: string
  displayName: string
  color: string
  badges: ChatBadge[]
  text: string
  emotes: ChatEmote[]
  isAction: boolean
  receivedAt: number
  publishedAt: number
  sourceChannelId?: string
  sourceVideoId?: string
  authorAvatarUrl?: string
  youtubeRoles?: YouTubeRoles
}

export interface YouTubeRoles {
  owner: boolean
  moderator: boolean
  member: boolean
  verified: boolean
}

export interface IrcLine {
  tags: Record<string, string>
  prefix: string
  command: string
  params: string[]
}

const TAG_ESCAPES: Record<string, string> = {
  ':': ';',
  s: ' ',
  '\\': '\\',
  r: '\r',
  n: '\n'
}

const unescapeTagValue = (value: string): string =>
  value.replace(/\\(.?)/g, (_, c: string) => TAG_ESCAPES[c] ?? c)

const parseTags = (part: string): Record<string, string> =>
  part.split(';').reduce<Record<string, string>>((acc, kv) => {
    if (kv === '') return acc
    const eq = kv.indexOf('=')
    if (eq === -1) return { ...acc, [kv]: '' }
    return { ...acc, [kv.slice(0, eq)]: unescapeTagValue(kv.slice(eq + 1)) }
  }, {})

const parseParams = (rest: string): string[] => {
  if (rest === '') return []
  if (rest.startsWith(':')) return [rest.slice(1)]
  const sp = rest.indexOf(' ')
  if (sp === -1) return [rest]
  return [rest.slice(0, sp), ...parseParams(rest.slice(sp + 1))]
}

export const parseIrcLine = (raw: string): IrcLine | null => {
  let rest = raw
  let tags: Record<string, string> = {}
  if (rest.startsWith('@')) {
    const sp = rest.indexOf(' ')
    if (sp === -1) return null
    tags = parseTags(rest.slice(1, sp))
    rest = rest.slice(sp + 1)
  }
  let prefix = ''
  if (rest.startsWith(':')) {
    const sp = rest.indexOf(' ')
    if (sp === -1) return null
    prefix = rest.slice(1, sp)
    rest = rest.slice(sp + 1)
  }
  const sp = rest.indexOf(' ')
  const command = sp === -1 ? rest : rest.slice(0, sp)
  if (command === '') return null
  const params = sp === -1 ? [] : parseParams(rest.slice(sp + 1))
  return { tags, prefix, command, params }
}

export const parseBadgesTag = (value: string): ChatBadge[] => {
  if (value === '') return []
  return value.split(',').flatMap((pair) => {
    const slash = pair.indexOf('/')
    if (slash <= 0 || slash === pair.length - 1) return []
    return [{ set: pair.slice(0, slash), version: pair.slice(slash + 1) }]
  })
}

export const parseEmotesTag = (value: string): ChatEmote[] => {
  if (value === '') return []
  return value
    .split('/')
    .flatMap((group) => {
      const colon = group.indexOf(':')
      if (colon <= 0) return []
      const id = group.slice(0, colon)
      return group.slice(colon + 1).split(',').flatMap((range) => {
        const [startRaw, endRaw] = range.split('-')
        const start = Number(startRaw)
        const end = Number(endRaw)
        if (!Number.isInteger(start) || !Number.isInteger(end) || end < start) return []
        return [{ id, start, end }]
      })
    })
    .sort((a, b) => a.start - b.start)
}

const TWITCH_NAME_COLORS = [
  '#FF0000', '#0000FF', '#008000', '#B22222', '#FF7F50',
  '#9ACD32', '#FF4500', '#2E8B57', '#DAA520', '#D2691E',
  '#5F9EA0', '#1E90FF', '#FF69B4', '#8A2BE2', '#00FF7F'
]

export const fallbackColor = (login: string): string => {
  if (login === '') return TWITCH_NAME_COLORS[0]
  const n = login.charCodeAt(0) + login.charCodeAt(login.length - 1)
  return TWITCH_NAME_COLORS[n % TWITCH_NAME_COLORS.length]
}

const ACTION_PREFIX = '\u0001ACTION '

const stripAction = (raw: string): { text: string, isAction: boolean } => {
  if (!raw.startsWith(ACTION_PREFIX) || !raw.endsWith('\u0001')) {
    return { text: raw, isAction: false }
  }
  return { text: raw.slice(ACTION_PREFIX.length, -1), isAction: true }
}

export const parsePrivmsg = (line: IrcLine): ChatMessage | null => {
  const channelParam = line.params[0]
  const rawText = line.params[1]
  if (channelParam == null || rawText == null) return null
  const login = line.prefix.split('!')[0]
  if (login === '') return null
  const { text, isAction } = stripAction(rawText)
  const displayName = line.tags['display-name'] ?? ''
  const tagColor = line.tags.color ?? ''
  const id = line.tags.id ?? ''
  const receivedAt = Date.now()
  const sentTs = Number(line.tags['tmi-sent-ts'] ?? '')
  return {
    id: id === '' ? `${line.tags['tmi-sent-ts'] ?? '0'}:${login}:${text}` : id,
    platform: 'TWITCH',
    channel: channelParam.replace(/^#/, ''),
    roomId: line.tags['room-id'] ?? '',
    login,
    displayName: displayName === '' ? login : displayName,
    color: tagColor === '' ? fallbackColor(login) : tagColor,
    badges: parseBadgesTag(line.tags.badges ?? ''),
    text,
    emotes: parseEmotesTag(line.tags.emotes ?? ''),
    isAction,
    receivedAt,
    publishedAt: Number.isFinite(sentTs) && sentTs > 0 ? sentTs : receivedAt
  }
}

export type MessageSegment =
  | { kind: 'text', text: string }
  | { kind: 'emote', id: string, alt: string }

export const messageSegments = (text: string, emotes: ChatEmote[]): MessageSegment[] => {
  if (emotes.length === 0) return text === '' ? [] : [{ kind: 'text', text }]
  const points = Array.from(text)
  const walked = emotes.reduce<{ segments: MessageSegment[], cursor: number }>(
    (acc, emote) => {
      if (emote.start < acc.cursor || emote.end >= points.length) return acc
      const before = points.slice(acc.cursor, emote.start).join('')
      const alt = points.slice(emote.start, emote.end + 1).join('')
      return {
        segments: [
          ...acc.segments,
          ...(before === '' ? [] : [{ kind: 'text' as const, text: before }]),
          { kind: 'emote' as const, id: emote.id, alt }
        ],
        cursor: emote.end + 1
      }
    },
    { segments: [], cursor: 0 }
  )
  const tail = points.slice(walked.cursor).join('')
  if (tail === '') return walked.segments
  return [...walked.segments, { kind: 'text', text: tail }]
}

export const emoteUrl = (id: string): string =>
  `https://static-cdn.jtvnw.net/emoticons/v2/${id}/default/dark/2.0`
